import { motion } from "framer-motion";
import { Target, Eye, Rocket, Award, TrendingUp, Users, Globe2, Lightbulb, CheckCircle2, Zap, Leaf, Shield, Sparkles, ArrowRight, Star, Layers } from "lucide-react";

export function MissionVision() {
  return (
    <section id="mission" className="relative py-12 lg:py-16 overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="mvGrid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#mvGrid)" />
        </svg>
      </div>

      {/* Decorative Blobs */}
      <div className="absolute top-20 left-0 w-96 h-96 bg-[#1976D2]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-[#1976D2]/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Professional Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-10"
        >
          {/* Top Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex justify-center mb-6"
          >
            <div className="inline-flex items-center gap-2 bg-white border-2 border-gray-200 rounded-full px-6 py-3 shadow-lg">
              <Layers className="w-5 h-5 text-[#7CB342]" />
              <span className="text-sm font-bold text-gray-900">Our Mission & Vision</span>
            </div>
          </motion.div>

          {/* Main Hero Section - Split Panel Design */}
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-center mb-6">
            {/* Left - Main Title & Tagline */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              {/* Decorative Element */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute -top-8 -left-8 w-16 h-16 lg:w-24 lg:h-24 bg-gradient-to-br from-[#7CB342]/20 to-[#1976D2]/20 rounded-full blur-2xl"
              />

              <div className="relative">
                {/* Overline */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="mb-6"
                >
                  <div className="inline-flex items-center gap-3">
                    <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#7CB342]" />
                    <span className="text-sm font-bold text-[#7CB342] uppercase tracking-wider">Powering Change</span>
                  </div>
                </motion.div>

                {/* Main Headline */}
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="mb-8"
                >
                  <div className="text-5xl lg:text-6xl xl:text-7xl font-black leading-none mb-4">
                    <span className="text-gray-900 block mb-2">Driving the</span>
                    <span className="text-gray-900 block mb-2">Future of</span>
                    <span className="bg-gradient-to-r from-[#7CB342] via-[#1976D2] to-[#7CB342] bg-clip-text text-transparent bg-[length:200%_100%] animate-[gradient_4s_ease-in-out_infinite]">
                      Clean Energy
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-6">
                    <span className="text-3xl lg:text-4xl xl:text-5xl font-black text-gray-900">in India</span>
                    <motion.div
                      animate={{ x: [0, 10, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="hidden lg:block"
                    >
                      <ArrowRight className="w-10 h-10 lg:w-12 lg:h-12 text-[#7CB342]" />
                    </motion.div>
                  </div>
                </motion.h1>

                {/* Tagline */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="relative"
                >
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#7CB342] to-[#1976D2] rounded-2xl opacity-20 blur" />
                  <div className="relative bg-white/80 backdrop-blur-sm border-2 border-gray-200 rounded-2xl p-6 lg:p-8">
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-[#7CB342] to-[#1976D2] rounded-xl p-3 flex-shrink-0">
                        <Zap className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-xl lg:text-2xl font-black text-gray-900 mb-2">
                          Where Waste Becomes Power
                        </p>
                        <p className="text-base text-gray-600 leading-relaxed">
                          Transforming India's waste ecosystem into sustainable energy solutions through innovation and technology
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Stats Row */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="grid grid-cols-3 gap-4 mt-8"
                >
                  {[
                    { icon: Leaf, label: "Sustainable", color: "from-[#7CB342] to-[#689F38]" },
                    { icon: Shield, label: "Certified", color: "from-[#1976D2] to-[#1565C0]" },
                    { icon: TrendingUp, label: "Scalable", color: "from-purple-500 to-indigo-600" },
                  ].map((item, index) => (
                    <div key={index} className={`bg-gradient-to-br ${item.color} rounded-xl p-4 text-center text-white shadow-lg hover:scale-105 transition-transform`}>
                      <item.icon className="w-6 h-6 mx-auto mb-2" />
                      <div className="text-xs font-bold">{item.label}</div>
                    </div>
                  ))}
                </motion.div>
              </div>
            </motion.div>

            {/* Right - Visual Element */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative">
                {/* Main Image Card */}
                <div className="absolute -inset-2 bg-gradient-to-r from-[#7CB342] to-[#1976D2] rounded-3xl opacity-30 blur-2xl" />
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://images.unsplash.com/photo-1709195325979-efb62c1f01a8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMGVuZXJneSUyMGluZGlhJTIwc29sYXJ8ZW58MXx8fHwxNzY1MDkzNjc1fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                    alt="Clean Energy in India"
                    className="w-full h-[400px] lg:h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/20 to-transparent" />
                  
                  {/* Overlay Stats */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                    <div className="bg-white/95 backdrop-blur-md rounded-2xl p-6 shadow-2xl">
                      <div className="grid grid-cols-3 gap-4">
                        {[
                          { label: "Collection Network", value: "Pan-India", icon: Globe2 },
                          { label: "ISCC Ready", value: "100%", icon: Award },
                          { label: "IoT Enabled", value: "Yes", icon: Zap },
                        ].map((stat, index) => (
                          <div key={index} className="text-center">
                            <stat.icon className="w-5 h-5 text-[#7CB342] mx-auto mb-2" />
                            <div className="text-xs text-gray-600 font-semibold mb-1">{stat.label}</div>
                            <div className="text-lg font-black text-gray-900">{stat.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Badge */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-6 -right-6 bg-gradient-to-br from-[#7CB342] to-[#689F38] border-4 border-white rounded-2xl p-4 shadow-2xl"
                >
                  <Star className="w-8 h-8 text-white mb-1" />
                  <div className="text-xs font-bold text-white text-center">ISCC Certified</div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Mission & Vision Cards */}
        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          {/* Mission Card */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="group relative"
          >
            <div className="relative h-full">
              {/* Animated Border */}
              <div className="absolute -inset-1 bg-gradient-to-r from-[#7CB342] to-[#689F38] rounded-2xl opacity-20 group-hover:opacity-40 blur-xl transition-all" />
              
              {/* Card */}
              <div className="relative bg-white rounded-2xl p-6 lg:p-8 shadow-xl h-full border-2 border-gray-100 group-hover:border-transparent transition-all">
                {/* Icon Badge */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-[#7CB342] rounded-xl blur-lg opacity-50" />
                    <div className="relative bg-gradient-to-br from-[#7CB342] to-[#689F38] rounded-xl p-3 shadow-xl">
                      <Target className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">01</div>
                    <h3 className="text-2xl lg:text-3xl font-black text-gray-900">Our Mission</h3>
                  </div>
                </div>

                {/* Content */}
                <p className="text-base text-gray-700 leading-relaxed mb-6">
                  To transform India's waste management ecosystem by establishing the most comprehensive{" "}
                  <span className="font-bold text-[#7CB342]">Used Cooking Oil collection network</span>, 
                  converting post-consumer and industrial waste into{" "}
                  <span className="font-bold text-[#7CB342]">ISCC-certified renewable fuel feedstock</span>{" "}
                  that powers the global transition to clean energy.
                </p>

                {/* Mission Points */}
                <div className="space-y-2.5 mb-6">
                  {[
                    "Build India's largest UCO aggregation network",
                    "Enable 100% traceable supply chains",
                    "Support biofuel manufacturers globally",
                    "Drive circular economy adoption",
                  ].map((point, index) => (
                    <div key={index} className="flex items-start gap-2.5">
                      <div className="bg-gradient-to-br from-[#7CB342] to-[#689F38] rounded-lg p-1 mt-0.5 flex-shrink-0">
                        <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                      </div>
                      <span className="text-sm text-gray-700 font-medium flex-1">{point}</span>
                    </div>
                  ))}
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 pt-4 border-t-2 border-gray-100">
                  {[
                    { icon: Users, label: "Partners", value: "1000+" },
                    { icon: Globe2, label: "Coverage", value: "Pan-India" },
                  ].map((item, index) => (
                    <div key={index} className="bg-gradient-to-br from-[#7CB342]/5 to-transparent rounded-xl p-3 hover:from-[#7CB342]/10 transition-all group/stat">
                      <item.icon className="w-5 h-5 text-[#7CB342] mb-1.5 group-hover/stat:scale-110 transition-transform" />
                      <div className="text-xs text-gray-600 font-semibold mb-0.5">{item.label}</div>
                      <div className="text-lg font-black text-gray-900">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Vision Card */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="group relative"
          >
            <div className="relative h-full">
              {/* Animated Border */}
              <div className="absolute -inset-1 bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-2xl opacity-20 group-hover:opacity-40 blur-xl transition-all" />
              
              {/* Card */}
              <div className="relative bg-white rounded-2xl p-6 lg:p-8 shadow-xl h-full border-2 border-gray-100 group-hover:border-transparent transition-all">
                {/* Icon Badge */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-[#1976D2] rounded-xl blur-lg opacity-50" />
                    <div className="relative bg-gradient-to-br from-[#1976D2] to-[#1565C0] rounded-xl p-3 shadow-xl">
                      <Eye className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">02</div>
                    <h3 className="text-2xl lg:text-3xl font-black text-gray-900">Our Vision</h3>
                  </div>
                </div>

                {/* Content */}
                <p className="text-base text-gray-700 leading-relaxed mb-6">
                  To become{" "}
                  <span className="font-bold text-[#1976D2]">Asia's most trusted</span>{" "}
                  renewable feedstock aggregator, pioneering{" "}
                  <span className="font-bold text-[#1976D2]">100% digitally traceable</span>{" "}
                  supply chains that enable India to lead the global circular economy revolution and achieve{" "}
                  <span className="font-bold text-[#1976D2]">Net-Zero emissions targets</span>.
                </p>

                {/* Vision Goals */}
                <div className="space-y-2.5 mb-6">
                  {[
                    { icon: Lightbulb, text: "Pioneer IoT-enabled traceability" },
                    { icon: Globe2, text: "Lead Asia's renewable feedstock market" },
                    { icon: Award, text: "Set global certification standards" },
                    { icon: TrendingUp, text: "Scale circular economy practices" },
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3 bg-gradient-to-r from-[#1976D2]/5 to-transparent rounded-xl p-3 hover:from-[#1976D2]/10 transition-all group/item">
                      <div className="bg-gradient-to-br from-[#1976D2] to-[#1565C0] rounded-lg p-2 group-hover/item:scale-110 transition-transform flex-shrink-0">
                        <item.icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm text-gray-700 font-semibold">{item.text}</span>
                    </div>
                  ))}
                </div>

                {/* Future Outlook */}
                <div className="grid grid-cols-2 gap-3 pt-4 border-t-2 border-gray-100">
                  {[
                    { icon: Award, label: "Standard", value: "ISCC+" },
                    { icon: TrendingUp, label: "Impact", value: "Net-Zero" },
                  ].map((item, index) => (
                    <div key={index} className="bg-gradient-to-br from-[#1976D2]/5 to-transparent rounded-xl p-3 hover:from-[#1976D2]/10 transition-all group/stat">
                      <item.icon className="w-5 h-5 text-[#1976D2] mb-1.5 group-hover/stat:scale-110 transition-transform" />
                      <div className="text-xs text-gray-600 font-semibold mb-0.5">{item.label}</div>
                      <div className="text-lg font-black text-gray-900">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Impact Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -inset-1 bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-3xl opacity-30 blur-2xl" />
          <div className="relative bg-gradient-to-r from-[#1976D2] to-[#1565C0] rounded-3xl p-10 lg:p-12 text-center overflow-hidden shadow-2xl">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24" />

            {/* Content */}
            <div className="relative">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-md rounded-full px-6 py-3 mb-6"
              >
                <Rocket className="w-5 h-5 text-white" />
                <span className="text-white font-bold">Our Commitment</span>
              </motion.div>

              <h3 className="text-3xl lg:text-4xl font-black text-white mb-4 leading-tight">
                Transforming Every Litre into Clean Energy
              </h3>

              <p className="text-base lg:text-lg text-white/95 max-w-3xl mx-auto leading-relaxed">
                <span className="font-bold">KRB Clean Energy Private Limited</span> is committed to building a sustainable future where waste becomes a valuable resource, powering India's journey toward energy independence and environmental responsibility
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </section>
  );
}